import { Injectable, inject } from '@angular/core';
import { DelegateV2 } from '@delegatexyz/sdk';
import { http } from 'viem';
import { mainnet } from 'viem/chains';
import { PlatformService } from './platform.service';
import { environment } from '../../environments/environment';

// Types for Delegate.xyz
export interface DelegationInfo {
  type: string;
  to: string;
  from: string;
  contract: string;
  rights: string;
  tokenId: number;
  amount?: number;
}

export interface DelegationRights {
  hasAllRights: boolean;
  hasContractRights: boolean;
  hasTokenRights: boolean;
  delegations: DelegationInfo[];
}

@Injectable({
  providedIn: 'root'
})
export class DelegateXYZService {
  private platformService = inject(PlatformService);
  private delegateV2Client: InstanceType<typeof DelegateV2> | null = null;

  constructor() {
    this.initializeDelegateClient();
  }

  /**
   * Initialize the Delegate.xyz v2 client
   */
  private initializeDelegateClient(): void {
    if (this.platformService.isServer) {
      return;
    }

    try {
      // Use the RPC provider from environment
      const rpcUrl = environment.rpcHttpProvider || 'https://ethereum-rpc.publicnode.com';

      // Create transport without specifying chain to avoid version conflicts
      this.delegateV2Client = new DelegateV2({
        userTransport: http(rpcUrl) as any,
      });

      console.log('DelegateXYZService: Delegate.xyz v2 client initialized');
    } catch (error) {
      console.error('DelegateXYZService: Failed to initialize Delegate.xyz client:', error);
      // Set client to null to indicate initialization failed
      this.delegateV2Client = null;
    }
  }

  /**
   * Check if a user has delegation rights for another address (all rights)
   * @param delegator The address that would be delegating (from)
   * @param delegate The address that would be delegated to (to)
   * @returns Promise<boolean> True if delegation rights exist for all rights
   */
  async hasDelegationRights(delegator: string, delegate: string): Promise<boolean> {
    if (!this.delegateV2Client) {
      console.warn('DelegateXYZService: Delegate client not initialized');
      return false;
    }

    try {
      // Use checkDelegateForAll to verify if delegator has delegated all rights to delegate
      const hasRights = await this.delegateV2Client.checkDelegateForAll(
        delegate as `0x${string}`, // to
        delegator as `0x${string}`  // from
      );
      return hasRights;
    } catch (error) {
      console.error('DelegateXYZService: Error checking delegation rights:', error);
      return false;
    }
  }

  /**
   * Get all delegations for a specific user (both incoming and outgoing)
   * @param userAddress The address to get delegations for
   * @returns Promise<string[]> Array of addresses that the user has delegated to or received delegations from
   */
  async getUserDelegations(userAddress: string): Promise<string[]> {
    if (!this.delegateV2Client) {
      console.warn('DelegateXYZService: Delegate client not initialized');
      return [];
    }

    try {
      const delegations: string[] = [];

      // Get outgoing delegations (where user is the delegator)
      const outgoingDelegations = await this.delegateV2Client.getOutgoingDelegations(
        userAddress as `0x${string}`
      );

      // Get incoming delegations (where user is the delegate)
      const incomingDelegations = await this.delegateV2Client.getIncomingDelegations(
        userAddress as `0x${string}`
      );

      // Extract unique addresses from delegations
      const outgoingAddresses = outgoingDelegations.map((d: DelegationInfo) => d.to);
      const incomingAddresses = incomingDelegations.map((d: DelegationInfo) => d.from);

      // Combine and deduplicate addresses
      const allAddresses = [...outgoingAddresses, ...incomingAddresses];
      const uniqueAddresses = Array.from(new Set(allAddresses));

      return uniqueAddresses;
    } catch (error) {
      console.error('DelegateXYZService: Error getting user delegations:', error);
      return [];
    }
  }

  /**
   * Check if a user has delegation rights for a specific ERC721 token
   * @param delegator The address that would be delegating (from)
   * @param delegate The address that would be delegated to (to)
   * @param contractAddress The contract address of the token
   * @param tokenId The token ID
   * @returns Promise<boolean> True if delegation rights exist for the specific token
   */
  async hasDelegationRightsForToken(
    delegator: string,
    delegate: string,
    contractAddress: string,
    tokenId: string
  ): Promise<boolean> {
    if (!this.delegateV2Client) {
      console.warn('DelegateXYZService: Delegate client not initialized');
      return false;
    }

    try {
      // Check for ERC721 specific delegation rights
      const hasRights = await this.delegateV2Client.checkDelegateForERC721(
        delegate as `0x${string}`,      // to
        delegator as `0x${string}`,     // from
        contractAddress as `0x${string}`, // contract
        parseInt(tokenId, 10)           // tokenId
      );
      return hasRights;
    } catch (error) {
      console.error('DelegateXYZService: Error checking token-specific delegation rights:', error);
      return false;
    }
  }

  /**
   * Get detailed delegation information for a user
   * @param userAddress The address to get delegation info for
   * @returns Promise<DelegationRights> Detailed delegation information
   */
  async getDetailedDelegationInfo(userAddress: string): Promise<DelegationRights> {
    if (!this.delegateV2Client) {
      console.warn('DelegateXYZService: Delegate client not initialized');
      return {
        hasAllRights: false,
        hasContractRights: false,
        hasTokenRights: false,
        delegations: []
      };
    }

    try {
      const [outgoingDelegations, incomingDelegations] = await Promise.all([
        this.delegateV2Client.getOutgoingDelegations(userAddress as `0x${string}`),
        this.delegateV2Client.getIncomingDelegations(userAddress as `0x${string}`)
      ]);

      const allDelegations = [...outgoingDelegations, ...incomingDelegations];

      return {
        hasAllRights: allDelegations.some(d => d.type === 'ALL'),
        hasContractRights: allDelegations.some(d => d.type === 'CONTRACT'),
        hasTokenRights: allDelegations.some(d => d.type === 'ERC721' || d.type === 'ERC20' || d.type === 'ERC1155'),
        delegations: allDelegations
      };
    } catch (error) {
      console.error('DelegateXYZService: Error getting detailed delegation info:', error);
      return {
        hasAllRights: false,
        hasContractRights: false,
        hasTokenRights: false,
        delegations: []
      };
    }
  }

  /**
   * Check delegation rights for a specific contract (not token-specific)
   * @param delegator The address that would be delegating
   * @param delegate The address that would be delegated to
   * @param contractAddress The contract address
   * @returns Promise<boolean> True if delegation rights exist for the contract
   */
  async hasDelegationRightsForContract(
    delegator: string,
    delegate: string,
    contractAddress: string
  ): Promise<boolean> {
    if (!this.delegateV2Client) {
      console.warn('DelegateXYZService: Delegate client not initialized');
      return false;
    }

    try {
      const hasRights = await this.delegateV2Client.checkDelegateForContract(
        delegate as `0x${string}`,      // to
        delegator as `0x${string}`,     // from
        contractAddress as `0x${string}` // contract
      );
      return hasRights;
    } catch (error) {
      console.error('DelegateXYZService: Error checking contract delegation rights:', error);
      return false;
    }
  }

  /**
   * Get all outgoing delegations from a user
   * @param userAddress The address to get outgoing delegations for
   * @returns Promise<DelegationInfo[]> Array of outgoing delegations
   */
  async getOutgoingDelegations(userAddress: string): Promise<DelegationInfo[]> {
    if (!this.delegateV2Client) {
      console.warn('DelegateXYZService: Delegate client not initialized');
      return [];
    }

    try {
      return await this.delegateV2Client.getOutgoingDelegations(
        userAddress as `0x${string}`
      );
    } catch (error) {
      console.error('DelegateXYZService: Error getting outgoing delegations:', error);
      return [];
    }
  }

  /**
   * Get all incoming delegations to a user
   * @param userAddress The address to get incoming delegations for
   * @returns Promise<DelegationInfo[]> Array of incoming delegations
   */
  async getIncomingDelegations(userAddress: string): Promise<DelegationInfo[]> {
    if (!this.delegateV2Client) {
      console.warn('DelegateXYZService: Delegate client not initialized');
      return [];
    }

    try {
      return await this.delegateV2Client.getIncomingDelegations(
        userAddress as `0x${string}`
      );
    } catch (error) {
      console.error('DelegateXYZService: Error getting incoming delegations:', error);
      return [];
    }
  }
}
